import { useState, useEffect, useRef } from 'react';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Button } from '@/components/ui/button';
import { UniversalWindow } from '@/components/ui/UniversalWindow';
import { CameraCaptureDialog } from '@/components/media/CameraCaptureDialog';
import { captureLocation } from '@/lib/geolocation';
import { getSDK } from '@/store/sdkStore';
import { matchExtensionForField, loadFieldExtensions } from '@/lib/fieldExtensions';
import { AddressField } from '@/components/forms/AddressField';
import { HelpHint } from '@/components/ui/HelpHint';
import type { FieldMeta } from './FormFieldRenderer';


export function resolveUIType(dataType: string, refTable?: string): string {
  // ПЕРЕЧИСЛЕНИЕ — ДО проверки refTable: у enum refTable = КОД перечисления
  // (не таблица-источник), иначе поле уходило в ветку «ссылка» (пикер вместо
  // селекта значений) — селект со значениями не показывался.
  if (dataType === 'enum' || dataType === 'enumeration') return 'enum';
  if (refTable || dataType === 'reference') return 'reference';
  // «Любая ссылка» (anyref) — ссылочный тип: «...» → пикер «тип → значение»
  if (dataType === 'anyref') return 'reference';
  switch (dataType) {
    case 'text': return 'text';
    // Секретный текст (шифруется at-rest) — обычный ввод; расшифровка на сервере.
    case 'secret_text': return 'text';
    case 'numeric':
    case 'money':
      return 'number';
    case 'integer': return 'integer';
    case 'uuid': return refTable ? 'reference' : 'uuid';
    case 'date': return 'date';
    case 'boolean': return 'boolean';
    case 'timestamp': return 'timestamp';
    case 'enum':
    case 'enumeration':
      return 'enum';
    case 'json':
    case 'array':
      return 'json';
    case 'file': return 'file';
    // Реквизит ФОРМЫ «Строка» (дизайнер) = текстовое поле платформы
    case 'string': return 'text';
    default: return 'text';
  }
}

/** Форматирование числа */
export function formatNumber(v: unknown, type: string): string {
  const n = Number(v);
  if (isNaN(n) || n === 0) return String(v ?? '');
  if (type === 'money') {
    return new Intl.NumberFormat('ru-RU', { style: 'currency', currency: 'RUB' }).format(n);
  }
  if (type === 'integer') {
    return new Intl.NumberFormat('ru-RU', { maximumFractionDigits: 0 }).format(n);
  }
  return new Intl.NumberFormat('ru-RU', { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(n);
}

/** Парсинг даты из разных форматов → YYYY-MM-DD */
export function parseDateValue(v: unknown): string {
  let s = String(v ?? '');
  if (s.includes('T')) s = s.split('T')[0];
  if (/^\d{2}\.\d{2}\.\d{4}/.test(s)) {
    const [d, m, y] = s.split('.');
    s = `${y}-${m}-${d}`;
  }
  return s;
}

/** Парсинг timestamp → YYYY-MM-DDTHH:MM */
export function parseTimestampValue(v: unknown): string {
  let s = String(v ?? '');
  if (/^\d{2}\.\d{2}\.\d{4}/.test(s)) {
    const parts = s.split(' ');
    const [d, m, y] = parts[0].split('.');
    s = `${y}-${m}-${d}T${parts[1] || '00:00'}`;
  }
  if (/^\d{4}-\d{2}-\d{2}$/.test(s)) s += 'T00:00';
  if (s.includes('T') && s.length > 16) s = s.substring(0, 16);
  return s;
}

/** UUID (ссылочный UUID без refTable) — Input */
export function UuidField({ value, onChange, disabled }: { value: unknown; onChange: (v: unknown) => void; disabled?: boolean }) {
  return (
    <Input
      value={String(value ?? '')}
      onChange={(e) => onChange(e.target.value)}
      placeholder="00000000-0000-0000-0000-000000000000"
      disabled={disabled}
      className="h-8 text-sm font-mono"
    />
  );
}

/** Ссылочное поле — read-only Input + кнопки пикера/очистки.
 *  ЕДИНЫЙ механизм представления: ТОЛЬКО displayValue (серверный
 *  {col}_presentation — SSoT); заданная ссылка без представления (висячая) → «—».
 *  НИКАКИХ фоллбэков на UUID. */
export function ReferenceField({ value, displayValue, onChange, onPick, disabled, placeholder }: {
  value: unknown; displayValue?: string; onChange: (v: unknown) => void;
  onPick?: () => void; disabled?: boolean; placeholder?: string;
}) {
  const hasValue = value !== undefined && value !== null && value !== '';
  const showValue = displayValue || (hasValue ? '—' : '');
  return (
    <div className="flex gap-1 w-full">
      <Input
        value={showValue}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder || 'Выберите...'}
        disabled={disabled}
        className="h-8 text-sm flex-1 min-w-0"
        readOnly
      />
      {!disabled && onPick && (
        <Button size="sm" variant="outline" className="h-8 px-2 shrink-0" onClick={(e) => { e.stopPropagation(); onPick(); }}>...</Button>
      )}
      {!disabled && hasValue && (
        <Button size="sm" variant="ghost" className="h-8 px-1.5 shrink-0 text-muted-foreground hover:text-destructive"
          onClick={(e) => { e.stopPropagation(); onChange(''); }}>✕</Button>
      )}
    </div>
  );
}

/** Boolean (checkbox) */
export function toBool(v: unknown): boolean {
  if (v === true || v === 1 || v === '1' || v === 'true' || v === 'TRUE' || v === 'on' || v === 'yes') return true;
  return false;
}

export function BooleanField({ value, onChange, disabled }: { value: unknown; onChange: (v: unknown) => void; disabled?: boolean }) {
  return (
    <div className="flex items-center gap-2 py-1">
      <input
        type="checkbox"
        checked={toBool(value)}
        onChange={(e) => onChange(e.target.checked)}
        disabled={disabled}
        className="h-4 w-4 rounded border-gray-300"
      />
    </div>
  );
}

/** Enum (select) */
/** Enum-значение: строка (value=label) или {value, label} (перечисления 1С) */
export type EnumOption = string | { value: string; label: string };

export function EnumField({ value, options, onChange, disabled }: { value: unknown; options?: EnumOption[]; onChange: (v: unknown) => void; disabled?: boolean }) {
  const optValue = (o: EnumOption) => (typeof o === 'string' ? o : o.value);
  const optLabel = (o: EnumOption) => (typeof o === 'string' ? o : o.label);
  return (
    <select
      value={String(value ?? '')}
      onChange={(e) => onChange(e.target.value)}
      disabled={disabled}
      className="h-8 text-sm w-full rounded border bg-input px-2 py-1"
    >
      <option value="">—</option>
      {options?.map((opt) => <option key={optValue(opt)} value={optValue(opt)}>{optLabel(opt)}</option>)}
    </select>
  );
}

/** JSON/Array (textarea) */
export function JsonField({ value, onChange, disabled }: { value: unknown; onChange: (v: unknown) => void; disabled?: boolean }) {
  return (
    <textarea
      value={typeof value === 'string' ? value : JSON.stringify(value ?? null, null, 2)}
      onChange={(e) => onChange(e.target.value)}
      disabled={disabled}
      className="h-20 text-sm w-full rounded border bg-input px-2 py-1 font-mono resize-y"
    />
  );
}

// ===== Подсказка реквизита (как в 1С) =====
// Положения: '' (нет) | 'hover' (при наведении — title) | 'bottom' (под полем)
// | 'button' (кнопка «?» — ПОПАП НАД ЭЛЕМЕНТОМ, универсальный HelpHint).
export function HintWrap({ field, children }: { field: FieldMeta; children: React.ReactNode }) {
  const hint = field.hint || '';
  const pos = field.hint_position || '';
  if (!hint || pos === '') return <>{children}</>;
  if (pos === 'hover') {
    return <div title={hint}>{children}</div>;
  }
  if (pos === 'bottom') {
    return (
      <div className="space-y-0.5">
        {children}
        <div className="text-[10px] text-muted-foreground leading-snug">{hint}</div>
      </div>
    );
  }
  if (pos === 'button') {
    return (
      <div className="flex items-start gap-1">
        <div className="flex-1 min-w-0">{children}</div>
        <span className="mt-1.5 shrink-0">
          <HelpHint text={hint} />
        </span>
      </div>
    );
  }
  return <>{children}</>;
}

// ===== Main Component =====
